import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateCustomerProduct,
  AdminDeactivateCustomerProduct,
  AdminFetchCustomerProducts,
  AdminUpdateCustomerProduct,
} from 'src/constants/actionTypes'
import { CUSTOMER_PRODUCT_NAMES } from 'src/constants/customer-product/constants'
import type { CustomerProductName } from 'src/constants/customer/enums'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateCustomerProductPayload,
  CustomerProduct,
  DeactivateCustomerProductPayload,
  FetchCustomerProductsPayload,
  UpdateCustomerProductPayload,
} from 'src/interfaces/customerProduct'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

/**
 * Builds the whole product list for a customer.
 *
 * `GetPartnerProductsQuery` marks both `partnerId` and `product` `@IsNotEmpty()`, so there is
 * no way to ask for every product in one request — this fans out one GET per product name and
 * merges the results, exactly as Retool's `allOngoingProducts` does. ENG-732 tracks adding a
 * list-all variant, after which this collapses to a single call.
 *
 * `all()` means a single rejection fails the whole fetch. That is deliberate: a partial list
 * would silently under-report entitlements to the feature gates that read this slice, so an
 * admin would see "not entitled" for a product the customer actually has.
 */
export function* onFetchCustomerProducts(action: AsyncAction<FetchCustomerProductsPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchCustomerProducts.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const responses: Array<{ payload: unknown }> = yield all(
      CUSTOMER_PRODUCT_NAMES.map((product) => {
        return call(callSecureApi, Endpoints.AdminFetchPartnerProducts, {
          method: 'GET',
          query: { partnerId, product },
        })
      }),
    )

    // Each response is a raw array today; the normalizer also handles `{ data }`, so these
    // call sites stay correct if the endpoint is ever wrapped.
    const products = responses.flatMap((response) => {
      return normalizeApiListPayload<CustomerProduct>(response.payload)
    })

    yield put(AdminFetchCustomerProducts.success(products))

    if (action.next) {
      action.next(null, products)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.customerProduct.onFetchCustomerProducts] error',
    })

    yield put(AdminFetchCustomerProducts.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load products'))
    }
  }
}

export function* onCreateCustomerProduct(action: AsyncAction<CreateCustomerProductPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminCreateCustomerProduct.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { product, startTime, endTime } = action.payload

    // `endTime` is omitted rather than sent as undefined/null: the DTO marks it `@IsOptional`,
    // and an absent key is what "ongoing" means to the backend.
    const body: { product: CustomerProductName; startTime: string; endTime?: string } = {
      product,
      startTime,
    }

    if (endTime) {
      body.endTime = endTime
    }

    const { payload }: { payload: CustomerProduct } = yield call(
      callSecureApi,
      Endpoints.AdminCreatePartnerProduct,
      {
        method: 'POST',
        params: [{ field: ':partnerId', value: partnerId }],
        body,
      },
    )

    yield put(AdminCreateCustomerProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.customerProduct.onCreateCustomerProduct] error',
    })

    yield put(AdminCreateCustomerProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to add product'))
    }
  }
}

export function* onUpdateCustomerProduct(action: AsyncAction<UpdateCustomerProductPayload>) {
  const partnerId = action.payload?.partnerId
  const productId = action.payload?.productId

  if (!partnerId || !productId) {
    const message = !partnerId ? 'Missing partnerId' : 'Missing productId'

    yield put(AdminUpdateCustomerProduct.failed(new Error(message)))

    if (action.next) {
      action.next(message)
    }

    return
  }

  try {
    // The body is built explicitly rather than forwarded: `PartnerProductService.update` reads
    // only `productId` and `endTime`, so a stray `product` or `startTime` would be discarded
    // while appearing to the admin to have been saved.
    const { payload }: { payload: CustomerProduct } = yield call(
      callSecureApi,
      Endpoints.AdminUpdatePartnerProduct,
      {
        method: 'PUT',
        params: [{ field: ':partnerId', value: partnerId }],
        body: { productId, endTime: action.payload.endTime },
      },
    )

    yield put(AdminUpdateCustomerProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.customerProduct.onUpdateCustomerProduct] error',
    })

    yield put(AdminUpdateCustomerProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update product'))
    }
  }
}

/**
 * Deactivates a product. The route is a DELETE that takes a request BODY rather than params or
 * a query string, and it soft-ends the row by setting `endTime = now` — it does not delete.
 *
 * `productId` is always sent. With it omitted the backend resolves the row itself by product
 * name and date window, which is one more way for the wrong row to be ended.
 */
export function* onDeactivateCustomerProduct(
  action: AsyncAction<DeactivateCustomerProductPayload>,
) {
  const partnerId = action.payload?.partnerId
  const productId = action.payload?.productId

  if (!partnerId || !productId) {
    const message = !partnerId ? 'Missing partnerId' : 'Missing productId'

    yield put(AdminDeactivateCustomerProduct.failed(new Error(message)))

    if (action.next) {
      action.next(message)
    }

    return
  }

  try {
    const { payload }: { payload: CustomerProduct } = yield call(
      callSecureApi,
      Endpoints.AdminDeactivatePartnerProduct,
      {
        method: 'DELETE',
        params: [{ field: ':partnerId', value: partnerId }],
        body: { product: action.payload.product, productId },
      },
    )

    yield put(AdminDeactivateCustomerProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.customerProduct.onDeactivateCustomerProduct] error',
    })

    yield put(AdminDeactivateCustomerProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to deactivate product'))
    }
  }
}

export function* customerProductSaga() {
  // Re-triggered on every customer select and manual refresh; only the newest run matters.
  yield takeLatest(AdminFetchCustomerProducts.type.REQUEST, onFetchCustomerProducts)
  // Mutations must each run.
  yield takeEvery(AdminCreateCustomerProduct.type.REQUEST, onCreateCustomerProduct)
  yield takeEvery(AdminUpdateCustomerProduct.type.REQUEST, onUpdateCustomerProduct)
  yield takeEvery(AdminDeactivateCustomerProduct.type.REQUEST, onDeactivateCustomerProduct)
}
